import {
  NormalizedTheme,
  ThemeColorMap,
  ThemeFonts,
  ThemeRowData,
  ThemeSeed,
  ThemeSelection,
  ThemeTokens,
} from '@/types/theme';
import { getAccessibleForeground, getReadableMutedForeground } from './contrastUtils';

const DEFAULT_COLORS: ThemeColorMap = {
  card: '0 0% 100%',
  ring: '348 87% 91%',
  input: '30 100% 95%',
  muted: '30 50% 96%',
  accent: '348 87% 91%',
  border: '348 30% 90%',
  popover: '30 100% 98%',
  primary: '262 100% 84%',
  secondary: '220 69% 76%',
  background: '0 0% 100%',
  foreground: '0 0% 33%',
  destructive: '0 84.2% 60.2%',
  'card-foreground': '0 0% 33%',
  'muted-foreground': '0 0% 45%',
  'accent-foreground': '0 0% 33%',
  'popover-foreground': '0 0% 33%',
  'primary-foreground': '0 0% 33%',
  'secondary-foreground': '0 0% 33%',
  'destructive-foreground': '0 0% 98%',
};

const DEFAULT_FONTS: ThemeFonts = {
  general: 'Source Sans Pro, sans-serif',
  headings: 'Roboto, ui-sans-serif, system-ui, sans-serif',
};

const DEFAULT_THEME_SEED: ThemeSeed = {
  brand: '262 100% 84%',
  accent: '348 87% 91%',
  background: '0 0% 100%',
  surface: '0 0% 100%',
  text: '0 0% 33%',
  border: '348 30% 90%',
};

const DESTRUCTIVE_COLOR = '0 84.2% 60.2%';

type ParsedHsl = {
  h: number;
  s: number;
  l: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasStringValue(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function parseTokenHsl(value: string): ParsedHsl | null {
  const matches = value.match(/-?\d+(?:\.\d+)?/g);
  if (!matches || matches.length < 3) return null;

  const [h, s, l] = matches.slice(0, 3).map((part) => parseFloat(part));
  if ([h, s, l].some((part) => Number.isNaN(part))) return null;

  return {
    h: ((h % 360) + 360) % 360,
    s: clamp(s, 0, 100),
    l: clamp(l, 0, 100),
  };
}

function formatTokenHsl({ h, s, l }: ParsedHsl) {
  return `${Math.round(((h % 360) + 360) % 360)} ${Math.round(clamp(s, 0, 100))}% ${Math.round(clamp(l, 0, 100))}%`;
}

function normalizeHslToken(value: unknown, fallback: string): string {
  if (!hasStringValue(value)) {
    return fallback;
  }

  const parsed = parseTokenHsl(value.trim());
  return parsed ? formatTokenHsl(parsed) : fallback;
}

function normalizeColorMap(value: unknown): ThemeColorMap {
  if (!isRecord(value)) return {};

  const colors: ThemeColorMap = {};

  Object.entries(value).forEach(([key, color]) => {
    if (!hasStringValue(color)) return;
    const normalized = normalizeHslToken(color, '');
    if (!normalized) return;
    colors[key as keyof ThemeColorMap] = normalized;
  });

  return colors;
}

function cloneTokens(tokens: ThemeTokens): ThemeTokens {
  return {
    colors: { ...tokens.colors },
    fonts: tokens.fonts ? { ...tokens.fonts } : undefined,
  };
}

function pickFont(input: Record<string, unknown>, keys: string[], fallback: string) {
  const match = keys.find((key) => hasStringValue(input[key]));
  return match ? String(input[match]) : fallback;
}

export function normalizeThemeFonts(value: unknown, options?: { fillDefaults?: boolean }): ThemeFonts {
  const fillDefaults = options?.fillDefaults !== false;
  const fallbackGeneral = fillDefaults ? DEFAULT_FONTS.general : undefined;
  const fallbackHeadings = fillDefaults ? DEFAULT_FONTS.headings : undefined;

  if (!isRecord(value)) {
    return {
      general: fallbackGeneral,
      headings: fallbackHeadings,
    };
  }

  return {
    general: pickFont(value, ['general', 'sans'], fallbackGeneral || ''),
    headings: pickFont(value, ['headings', 'serif', 'heading'], fallbackHeadings || ''),
  };
}

function hasResolvedColors(colors: ThemeColorMap) {
  return Object.keys(colors).length > 0;
}

function mixHslColors(colorA: string, colorB: string, ratioToB: number) {
  const a = parseTokenHsl(colorA) || parseTokenHsl(DEFAULT_THEME_SEED.background)!;
  const b = parseTokenHsl(colorB) || parseTokenHsl(DEFAULT_THEME_SEED.surface)!;
  const weight = clamp(ratioToB, 0, 1);
  const delta = ((((b.h - a.h) % 360) + 540) % 360) - 180;

  return formatTokenHsl({
    h: a.h + delta * weight,
    s: a.s + (b.s - a.s) * weight,
    l: a.l + (b.l - a.l) * weight,
  });
}

export function deriveThemeSeedFromTokens(tokens: ThemeTokens): ThemeSeed {
  const colors = tokens.colors || {};

  return {
    brand: normalizeHslToken(colors.primary, DEFAULT_THEME_SEED.brand),
    accent: normalizeHslToken(colors.accent, DEFAULT_THEME_SEED.accent),
    background: normalizeHslToken(colors.background, DEFAULT_THEME_SEED.background),
    surface: normalizeHslToken(colors.card || colors.popover || colors.background, DEFAULT_THEME_SEED.surface),
    text: normalizeHslToken(colors.foreground, DEFAULT_THEME_SEED.text),
    border: normalizeHslToken(colors.border || colors.input, DEFAULT_THEME_SEED.border),
  };
}

export function normalizeThemeSeed(value: unknown, fallback: ThemeSeed = DEFAULT_THEME_SEED): ThemeSeed {
  if (!isRecord(value)) {
    return { ...fallback };
  }

  return {
    brand: normalizeHslToken(value.brand, fallback.brand),
    accent: normalizeHslToken(value.accent, fallback.accent),
    background: normalizeHslToken(value.background, fallback.background),
    surface: normalizeHslToken(value.surface, fallback.surface),
    text: normalizeHslToken(value.text, fallback.text),
    border: normalizeHslToken(value.border, fallback.border),
  };
}

export function resolveThemeTokensFromSeed(seed: ThemeSeed, fonts?: ThemeFonts): ThemeTokens {
  const normalizedSeed = normalizeThemeSeed(seed, DEFAULT_THEME_SEED);
  const themeFonts = {
    ...DEFAULT_FONTS,
    ...(fonts || {}),
  };

  const secondary = mixHslColors(normalizedSeed.brand, normalizedSeed.surface, 0.58);
  const muted = mixHslColors(normalizedSeed.text, normalizedSeed.background, 0.86);
  const mutedForeground = getReadableMutedForeground(normalizedSeed.background, normalizedSeed.text);
  const primaryForeground = getAccessibleForeground(normalizedSeed.brand, normalizedSeed.text);
  const accentForeground = getAccessibleForeground(normalizedSeed.accent, normalizedSeed.text);
  const destructiveForeground = getAccessibleForeground(DESTRUCTIVE_COLOR, normalizedSeed.text);

  return {
    colors: {
      background: normalizedSeed.background,
      foreground: normalizedSeed.text,
      card: normalizedSeed.surface,
      'card-foreground': normalizedSeed.text,
      popover: normalizedSeed.surface,
      'popover-foreground': normalizedSeed.text,
      primary: normalizedSeed.brand,
      'primary-foreground': primaryForeground,
      secondary,
      'secondary-foreground': normalizedSeed.text,
      muted,
      'muted-foreground': mutedForeground,
      accent: normalizedSeed.accent,
      'accent-foreground': accentForeground,
      destructive: DESTRUCTIVE_COLOR,
      'destructive-foreground': destructiveForeground,
      border: normalizedSeed.border,
      input: normalizedSeed.border,
      ring: normalizedSeed.brand,
      sidebar: normalizedSeed.surface,
      'sidebar-background': normalizedSeed.surface,
      'sidebar-foreground': normalizedSeed.text,
      'sidebar-border': normalizedSeed.border,
      'sidebar-primary': normalizedSeed.brand,
      'sidebar-primary-foreground': primaryForeground,
      'sidebar-accent': secondary,
      'sidebar-accent-foreground': normalizedSeed.text,
      'sidebar-ring': normalizedSeed.brand,
    },
    fonts: themeFonts,
  };
}

export function getDefaultThemeTokens(): ThemeTokens {
  return {
    colors: { ...DEFAULT_COLORS },
    fonts: { ...DEFAULT_FONTS },
  };
}

export function getDefaultThemeSeed(): ThemeSeed {
  return { ...DEFAULT_THEME_SEED };
}

export function normalizeThemeRow(theme: ThemeRowData | null | undefined): NormalizedTheme {
  if (!theme) {
    const tokens = getDefaultThemeTokens();
    return {
      seed: deriveThemeSeedFromTokens(tokens),
      tokens,
      fonts: { ...(tokens.fonts || DEFAULT_FONTS) },
      source: 'default',
    };
  }

  const fonts = normalizeThemeFonts(theme.fonts);
  const resolvedTokens: ThemeTokens = {
    colors: normalizeColorMap(theme.colors),
    fonts,
  };
  const derivedSeed = deriveThemeSeedFromTokens(
    hasResolvedColors(resolvedTokens.colors) ? resolvedTokens : getDefaultThemeTokens(),
  );
  const seed = normalizeThemeSeed(theme.seed, derivedSeed);
  const tokens = theme.seed
    ? resolveThemeTokensFromSeed(seed, fonts)
    : hasResolvedColors(resolvedTokens.colors)
      ? resolvedTokens
      : resolveThemeTokensFromSeed(seed, fonts);

  return {
    seed,
    tokens,
    fonts,
    source: theme.seed ? 'semantic-preset' : 'legacy-preset',
    themeId: theme.id,
    name: theme.name,
  };
}

export function normalizeThemeSelection(selection: ThemeSelection | null | undefined, presetTheme?: ThemeRowData | null): NormalizedTheme {
  if (!selection) {
    return normalizeThemeRow(null);
  }

  if (selection.type === 'preset') {
    return normalizeThemeRow(presetTheme);
  }

  const fonts = normalizeThemeFonts(selection.overrides?.fonts);
  const resolvedTokens: ThemeTokens = {
    colors: normalizeColorMap(selection.overrides?.colors),
    fonts,
  };
  const fallbackSeed = deriveThemeSeedFromTokens(
    hasResolvedColors(resolvedTokens.colors) ? resolvedTokens : getDefaultThemeTokens(),
  );
  const seed = normalizeThemeSeed(selection.seed, fallbackSeed);
  const tokens = selection.seed
    ? resolveThemeTokensFromSeed(seed, fonts)
    : hasResolvedColors(resolvedTokens.colors)
      ? resolvedTokens
      : resolveThemeTokensFromSeed(seed, fonts);

  return {
    seed,
    tokens,
    fonts,
    source: selection.seed ? 'semantic-custom' : 'legacy-custom',
    themeId: selection.theme_id,
  };
}

export function buildCustomThemeSelection(seed: ThemeSeed, fonts?: ThemeFonts): ThemeSelection {
  const tokens = resolveThemeTokensFromSeed(seed, fonts);

  return {
    type: 'custom',
    seed: normalizeThemeSeed(seed),
    overrides: cloneTokens(tokens),
  };
}
